import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import {
  Users, Building2, Package, ShoppingCart, Clock, CheckCircle,
  AlertCircle, Calendar, Filter, Search, Eye, TrendingUp,
  FileText, Truck, ArrowRight, RefreshCw
} from 'lucide-react';

const PRIMARY = '#0099FF';
const PRIMARY_DARK = '#0077CC';
const PRIMARY_LIGHT = 'rgba(0, 153, 255, 0.08)';

const MainWrapper = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API_URL}/dashboard/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) setStats(await res.json());
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchStats();
  }, [API_URL, token]);

  const statusColor = (estado) => {
    switch(estado) {
      case 1: return 'bg-amber-100 text-amber-800';
      case 2: return 'bg-green-100 text-green-800';
      case 3: return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const statusLabel = (estado) => {
    switch(estado) {
      case 1: return 'Abierto';
      case 2: return 'Confirmado';
      case 3: return 'Enviado';
      case 4: return 'Envío parcial';
      default: return 'Desconocido';
    }
  };

  const statusIcon = (estado) => {
    switch(estado) {
      case 1: return <Clock size={14} />;
      case 2: return <CheckCircle size={14} />;
      case 3: return <Package size={14} />;
      case 4: return <AlertCircle size={14} />;
      default: return <AlertCircle size={14} />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: PRIMARY }} />
      </div>
    );
  }

  const quickLinks = [
    { label: 'Usuarios', icon: Users, to: '/usuarios', desc: 'Gestión de cuentas' },
    { label: 'Clientes', icon: Building2, to: '/clientes', desc: 'Gestión de clientes' },
    { label: 'Productos', icon: Package, to: '/productos', desc: 'Catálogo de productos' },
  ];

  return (
    <div className="h-full flex">

      {/* ── SIDEBAR ──────────────────────────────── */}
      <aside className="w-80 bg-white border-r border-gray-200 flex flex-col flex-shrink-0">

        {/* Sidebar Header */}
        <div className="p-6 border-b border-gray-200 text-center">
          <img src="/logo.png" alt="ComprasSanitarias" className="h-20 w-auto mx-auto mb-2" />
          <p className="text-sm text-gray-500">
            {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">

          {/* User welcome */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: PRIMARY }}>
                {user?.nombrec?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">{user?.nombrec || user?.username}</p>
                <p className="text-xs text-gray-500">
                  {user?.permisos === 6 ? 'Super Admin' : user?.permisos === 1 ? 'Administrador' : 'Operador'}
                </p>
              </div>
            </div>
          </div>

          {/* Stats cards */}
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Resumen</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                <p className="text-2xl font-bold text-amber-700">{stats?.pedidosAbiertos || 0}</p>
                <p className="text-xs text-amber-600 mt-1">Ped. Abiertos</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                <p className="text-2xl font-bold text-green-700">{stats?.pedidosConfirmados || 0}</p>
                <p className="text-xs text-green-600 mt-1">Confirmados</p>
              </div>
            </div>
          </div>

          {/* Quick links */}
          <div className="p-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Acceso Rápido</h3>
            <div className="space-y-1">
              {quickLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="flex items-center justify-between px-3 py-3 rounded-lg hover:bg-gray-50 transition-all duration-200 group border border-transparent hover:border-gray-200"
                >
                  <div className="flex items-center space-x-3">
                    <link.icon size={18} className="text-gray-400 group-hover:text-blue-500" />
                    <div>
                      <span className="text-sm font-medium text-gray-700 block">{link.label}</span>
                      <span className="text-xs text-gray-400">{link.desc}</span>
                    </div>
                  </div>
                  <ArrowRight size={14} className="text-gray-300 group-hover:text-blue-500" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </aside>

      {/* ── MAIN CONTENT ────────────────────────── */}
      <main className="flex-1 overflow-hidden flex flex-col">

        {/* Header bar */}
        <div className="bg-white shadow-sm border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 rounded-full" style={{ backgroundColor: PRIMARY_LIGHT }}>
                <ShoppingCart className="w-6 h-6" style={{ color: PRIMARY }} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Pedidos Recientes</h2>
                <p className="text-sm text-gray-500">Últimos pedidos del sistema</p>
              </div>
            </div>
            <button
              onClick={() => {
                setLoading(true);
                fetch(`${API_URL}/dashboard/stats`, { headers: { 'Authorization': `Bearer ${token}` }})
                  .then(r => r.json()).then(setStats).finally(() => setLoading(false));
              }}
              className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center gap-2"
            >
              <RefreshCw size={14} /> Actualizar
            </button>
          </div>
        </div>

        {/* Orders table */}
        <div className="flex-1 overflow-auto p-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pedido</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {stats?.pedidosRecientes?.map((pedido) => (
                    <tr key={pedido.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Package className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm font-medium text-gray-900">#{pedido.npedido || pedido.id}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">{pedido.cliente_nombre}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-500">
                          <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                          {new Date(pedido.fecha).toLocaleDateString('es-ES')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColor(pedido.estado)}`}>
                          {statusIcon(pedido.estado)}
                          {statusLabel(pedido.estado)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => navigate(`/pedidos/nuevo?order=${pedido.id}`)}
                          className="text-gray-400 hover:text-blue-600 p-1 rounded transition-colors duration-200"
                          title="Ver detalles"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {(!stats?.pedidosRecientes || stats.pedidosRecientes.length === 0) && (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center">
                        <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-sm text-gray-500">No hay pedidos recientes</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MainWrapper;
